// src/components/categories/CategoryStats.jsx
import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme
} from '@mui/material';
import {
  Category as CategoryIcon,
  Folder as FolderIcon,
  FolderSpecial as FolderSpecialIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useCategories } from '../../context/CategoryContext';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useTranslation } from 'react-i18next';
const CategoryStats = () => {
  const theme = useTheme();
  const { categories, categoriesList } = useCategories();
  
  // Calculer les statistiques
  const stats = useMemo(() => {
    if (!categoriesList || categoriesList.length === 0) {
      return {
        totalCategories: 0,
        rootCategories: 0,
        subCategories: 0,
        activeCategories: 0,
        inactiveCategories: 0,
        maxDepth: 0,
        categoriesPerLevel: []
      };
    }

    const rootCats = categoriesList.filter(cat => !cat.parentId);
    const subCats = categoriesList.filter(cat => cat.parentId);
    const activeCats = categoriesList.filter(cat => cat.isActive);
    const inactiveCats = categoriesList.filter(cat => !cat.isActive);
    
    // Calculer la profondeur maximale
    const calculateDepth = (categoryId, visited = new Set()) => {
      if (visited.has(categoryId)) return 0; // Éviter les boucles infinies
      visited.add(categoryId);
      
      const children = categoriesList.filter(cat => cat.parentId === categoryId);
      if (children.length === 0) return 1;
      
      return 1 + Math.max(...children.map(child => calculateDepth(child.id, new Set(visited))));
    };

    const maxDepth = rootCats.length > 0
      ? Math.max(...rootCats.map(cat => calculateDepth(cat.id)))
      : 0;

    // Calculer le nombre de catégories par niveau
    const categoriesPerLevel = [];
    for (let i = 0; i < maxDepth; i++) {
      if (i === 0) {
        categoriesPerLevel.push({
          name: 'Niveau 1 (Racine)',
          count: rootCats.length
        });
      } else {
        const levelCategories = categoriesList.filter(cat => {
          let depth = 0;
          let currentCat = cat;
          while (currentCat.parentId) {
            depth++;
            currentCat = categoriesList.find(c => c.id === currentCat.parentId);
            if (!currentCat) break;
          }
          return depth === i;
        });
        
        categoriesPerLevel.push({
          name: `Niveau ${i + 1}`,
          count: levelCategories.length
        });
      }
    }

    return {
      totalCategories: categoriesList.length,
      rootCategories: rootCats.length,
      subCategories: subCats.length,
      activeCategories: activeCats.length,
      inactiveCategories: inactiveCats.length,
      maxDepth,
      categoriesPerLevel
    };
  }, [categoriesList]);

  // Données pour le graphique en camembert
  const pieChartData = [
    { name: 'Catégories racines', value: stats.rootCategories, color: theme.palette.primary.main },
    { name: 'Sous-catégories', value: stats.subCategories, color: theme.palette.secondary.main }
  ];

  // Données pour le graphique en barres
  const barChartData = stats.categoriesPerLevel;

  // Données pour le graphique d'état
  const statusChartData = [
    { name: 'Actives', value: stats.activeCategories, color: theme.palette.success.main },
    { name: 'Inactives', value: stats.inactiveCategories, color: theme.palette.error.main }
  ];

  // Animation pour les cartes
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };
  const { t } = useTranslation();
  return (
    <Box sx={{ py: 2 }}>
  <Typography variant="h4" gutterBottom component="h1" sx={{ mb: 4 }}>
    {t('categoryManagement.statistics.title')}
  </Typography>
  <motion.div
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <motion.div variants={itemVariants}>
          <Card 
            elevation={3}
            sx={{ 
              borderRadius: 2,
              height: '100%',
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
              color: 'white'
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <CategoryIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.totalCategories}
              </Typography>
              <Typography variant="subtitle1">
                {t('categoryManagement.statistics.totalCategories')}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <motion.div variants={itemVariants}>
          <Card 
            elevation={3}
            sx={{ 
              borderRadius: 2,
              height: '100%',
              background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.light} 90%)`,
              color: 'white'
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <FolderIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.rootCategories}
              </Typography>
              <Typography variant="subtitle1">
                {t('categoryManagement.statistics.rootCategories')}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <motion.div variants={itemVariants}>
          <Card 
            elevation={3}
            sx={{ 
              borderRadius: 2,
              height: '100%',
              background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.success.light} 90%)`,
              color: 'white'
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <FolderSpecialIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.subCategories}
              </Typography>
              <Typography variant="subtitle1">
                {t('categoryManagement.statistics.subCategories')}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <motion.div variants={itemVariants}>
          <Card 
            elevation={3}
            sx={{ 
              borderRadius: 2,
              height: '100%',
              background: `linear-gradient(45deg, ${theme.palette.info.main} 30%, ${theme.palette.info.light} 90%)`,
              color: 'white'
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <VisibilityIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {stats.activeCategories}
              </Typography>
              <Typography variant="subtitle1">
                {t('categoryManagement.statistics.activeCategories')}
              </Typography>
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
    </Grid>
  </motion.div>
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        elevation={3} 
        sx={{ p: 3, borderRadius: 2, height: 400 }}
      >
        <Typography variant="h6" gutterBottom>
          {t('categoryManagement.statistics.distributionTitle')}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ResponsiveContainer width="100%" height="90%">
          <PieChart>
            <Pie
              data={pieChartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [`${value} ${t('common.categories')}`, t('common.number')]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
    </Grid>
    <Grid item xs={12} md={6}>
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        elevation={3} 
        sx={{ p: 3, borderRadius: 2, height: 400 }}
      >
        <Typography variant="h6" gutterBottom>
          {t('categoryManagement.statistics.statusTitle')}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ResponsiveContainer width="100%" height="90%">
          <PieChart>
            <Pie
              data={statusChartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {statusChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [`${value} ${t('common.categories')}`, t('common.number')]} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Paper>
    </Grid>
    <Grid item xs={12}>
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
        elevation={3} 
        sx={{ p: 3, borderRadius: 2, height: 400, mt: 2 }}
      >
        <Typography variant="h6" gutterBottom>
          {t('categoryManagement.statistics.levelTitle')}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ResponsiveContainer width="100%" height="90%">
          <BarChart
            data={barChartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip formatter={(value) => [`${value} ${t('common.categories')}`, t('common.number')]} />
            <Legend />
            <Bar dataKey="count" name={t('common.categoryCount')} fill={theme.palette.primary.main} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Grid>
  </Grid>
</Box>
  );
};

export default CategoryStats;